/**
 * AdminFaqTab.jsx — CRUD for the AI chat FAQ knowledge base.
 *
 * Filtering: scope dropdown × language dropdown × active-only toggle.
 * Editing: inline modal that creates new or edits existing rows.
 * Seed: one-click "Seed default FAQs" button (idempotent on backend).
 *
 * Auth: requires the admin to enter the platform's `ADMIN_TOKEN` once
 * per session — stored in localStorage. If wrong, every backend call
 * returns 403 and we re-prompt.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, RefreshCw, Save, X, BookOpen, Filter, Power,
} from "lucide-react";

const LANGS = ["sk", "en", "cs", "de", "pl", "hu", "fr", "it", "es", "pt", "nl", "sv", "no", "da", "fi", "ro", "bg", "hr", "uk", "tr", "ja", "ko", "zh", "hi", "ar", "id"];
const TOKEN_KEY = "trp_admin_token";

const headers = () => ({ "X-Admin-Token": localStorage.getItem(TOKEN_KEY) || "" });

export default function AdminFaqTab() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [scopes, setScopes] = useState([]);
  const [filter, setFilter] = useState({ scope: "", lang: "", activeOnly: false });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, scope, lang, question, answer, weight, active}
  const [tokenError, setTokenError] = useState(false);

  const loadScopes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/faqs/scopes`, { headers: headers() });
      setScopes(r.data.scopes || []);
      setTokenError(false);
    } catch (e) {
      if (e?.response?.status === 403) setTokenError(true);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.scope) params.scope = filter.scope;
      if (filter.lang) params.lang = filter.lang;
      if (filter.activeOnly) params.active_only = true;
      const r = await axios.get(`${API}/admin/faqs`, { headers: headers(), params });
      setRows(r.data.rows || []);
      setTokenError(false);
    } catch (e) {
      if (e?.response?.status === 403) setTokenError(true);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    if (token) { loadScopes(); loadRows(); }
  }, [token, loadScopes, loadRows]);

  const saveToken = () => {
    localStorage.setItem(TOKEN_KEY, token.trim());
    setTokenError(false);
    loadScopes(); loadRows();
  };

  const startNew = () => setEditing({ scope: filter.scope || "global", lang: filter.lang || "sk", question: "", answer: "", weight: 1, active: true });
  const startEdit = (r) => setEditing({ ...r });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing.question?.trim() || !editing.answer?.trim()) {
      toast.error("Vyplň otázku aj odpoveď");
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/faqs/${editing.id}`, {
          scope: editing.scope, lang: editing.lang, question: editing.question,
          answer: editing.answer, weight: editing.weight, active: editing.active,
        }, { headers: headers() });
        toast.success("FAQ aktualizovaná");
      } else {
        await axios.post(`${API}/admin/faqs`, {
          scope: editing.scope || "global", lang: editing.lang || "sk",
          question: editing.question, answer: editing.answer,
          weight: editing.weight || 1, keywords: [],
        }, { headers: headers() });
        toast.success("FAQ pridaná");
      }
      setEditing(null);
      loadScopes(); loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skús to znova");
    }
  };

  const removeRow = async (r) => {
    if (!window.confirm(`Zmazať FAQ „${r.question.slice(0, 60)}…"?`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${r.id}`, { headers: headers() });
      toast.success("FAQ zmazaná");
      loadRows();
    } catch { toast.error("Zmazanie zlyhalo"); }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/admin/faqs/${r.id}`, { active: !r.active }, { headers: headers() });
      loadRows();
    } catch { toast.error("Update zlyhal"); }
  };

  const runSeed = async () => {
    try {
      const r = await axios.post(`${API}/admin/faqs/seed`, {}, { headers: headers() });
      toast.success(`Seed hotový: +${r.data.inserted}, skipped ${r.data.skipped}`);
      loadScopes(); loadRows();
    } catch { toast.error("Seed zlyhal"); }
  };

  if (tokenError || !token) {
    return (
      <div data-testid="admin-faqs-panel" className="max-w-md mx-auto p-6 bg-white border border-amber-300 rounded-lg">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2 text-gray-900">
          <BookOpen className="w-5 h-5" /> Chat FAQ Knowledge Base
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          Zadaj <code className="bg-gray-100 px-1">ADMIN_TOKEN</code> (z <code>backend/.env</code>) pre prístup.
        </p>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          data-testid="admin-faqs-token-input"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-2"
          placeholder="Admin token"
        />
        <button
          onClick={saveToken}
          data-testid="admin-faqs-token-save"
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-bold"
        >Uložiť a načítať</button>
        {tokenError && <p className="mt-2 text-xs text-rose-600">Neplatný token — skús znova.</p>}
      </div>
    );
  }

  return (
    <div data-testid="admin-faqs-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900">
          <BookOpen className="w-5 h-5" /> Chat FAQ Knowledge Base
          <span className="text-xs font-normal text-gray-500">({rows.length} FAQ-iek)</span>
        </h2>
        <div className="flex gap-2">
          <button
            onClick={runSeed}
            data-testid="admin-faqs-seed"
            className="px-3 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-xs font-bold uppercase tracking-widest flex items-center gap-1"
          ><RefreshCw className="w-3 h-3" /> Seed default</button>
          <button
            onClick={startNew}
            data-testid="admin-faqs-new"
            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1"
          ><Plus className="w-3 h-3" /> Nová FAQ</button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-gray-50 p-3 rounded">
        <Filter className="w-4 h-4 text-gray-500" />
        <select
          value={filter.scope}
          onChange={(e) => setFilter({ ...filter, scope: e.target.value })}
          data-testid="admin-faqs-filter-scope"
          className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
        >
          <option value="">Všetky scope</option>
          {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filter.lang}
          onChange={(e) => setFilter({ ...filter, lang: e.target.value })}
          data-testid="admin-faqs-filter-lang"
          className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
        >
          <option value="">Všetky jazyky</option>
          {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={filter.activeOnly}
            onChange={(e) => setFilter({ ...filter, activeOnly: e.target.checked })}
            data-testid="admin-faqs-filter-active"
          />
          Iba aktívne
        </label>
      </div>

      {loading && <div className="text-sm text-gray-500">Načítavam…</div>}

      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`admin-faqs-row-${r.id}`}
            className={`p-3 border rounded flex gap-3 items-start ${r.active ? "bg-white border-gray-200" : "bg-gray-50 border-gray-300 opacity-60"}`}
          >
            <div className="flex flex-col gap-1 min-w-[110px]">
              <span className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">{r.scope}</span>
              <span className="text-[10px] uppercase text-gray-500">{r.lang} · w{r.weight}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 mb-1">{r.question}</p>
              <p className="text-xs text-gray-600 line-clamp-2">{r.answer}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => toggleActive(r)} title={r.active ? "Vypnúť" : "Zapnúť"}
                data-testid={`admin-faqs-toggle-${r.id}`}
                className={`p-1 ${r.active ? "text-emerald-600" : "text-gray-400"} hover:bg-gray-100 rounded`}>
                <Power className="w-4 h-4" />
              </button>
              <button onClick={() => startEdit(r)} title="Editovať"
                data-testid={`admin-faqs-edit-${r.id}`}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => removeRow(r)} title="Zmazať"
                data-testid={`admin-faqs-delete-${r.id}`}
                className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">Žiadne FAQ-ky — klikni „Seed default" alebo „Nová FAQ".</div>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="admin-faqs-modal"
          onClick={cancelEdit}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{editing.id ? "Editovať FAQ" : "Nová FAQ"}</h3>
              <button onClick={cancelEdit} data-testid="admin-faqs-modal-close">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Scope</label>
                <input
                  type="text" value={editing.scope}
                  onChange={(e) => setEditing({ ...editing, scope: e.target.value })}
                  data-testid="admin-faqs-modal-scope"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                  list="faq-scopes-list"
                />
                <datalist id="faq-scopes-list">
                  {scopes.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Jazyk</label>
                <select
                  value={editing.lang}
                  onChange={(e) => setEditing({ ...editing, lang: e.target.value })}
                  data-testid="admin-faqs-modal-lang"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                >
                  {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Váha (1-10)</label>
                <input
                  type="number" min={1} max={10} value={editing.weight}
                  onChange={(e) => setEditing({ ...editing, weight: parseInt(e.target.value) || 1 })}
                  data-testid="admin-faqs-modal-weight"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900"
                />
              </div>
            </div>

            <label className="text-xs text-gray-600 block mb-1">Otázka</label>
            <input
              type="text" value={editing.question}
              onChange={(e) => setEditing({ ...editing, question: e.target.value })}
              data-testid="admin-faqs-modal-question"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-3"
              maxLength={300}
            />

            <label className="text-xs text-gray-600 block mb-1">Odpoveď</label>
            <textarea
              rows={6} value={editing.answer}
              onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
              data-testid="admin-faqs-modal-answer"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 mb-3"
              maxLength={2000}
            />

            {editing.id && (
              <label className="flex items-center gap-2 mb-4 text-sm text-gray-700">
                <input
                  type="checkbox" checked={editing.active !== false}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="admin-faqs-modal-active"
                /> Aktívna
              </label>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={cancelEdit}
                data-testid="admin-faqs-modal-cancel"
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50">Zrušiť</button>
              <button onClick={saveEdit}
                data-testid="admin-faqs-modal-save"
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-bold flex items-center gap-1 hover:bg-blue-700">
                <Save className="w-4 h-4" /> Uložiť
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
